import { isGroup, isRule } from '@/types/typeguards/QueryBuilder';
import type { IScope } from '@/types/filterBuilderTypes';
import { LIST_SEARCH_RULE_ID } from '@/utils/listSearch';

/** Recursively count leaf rules in a filter scope tree. */
export function countFilterRules(scope: IScope): number {
  if (isRule(scope)) {
    return 1;
  }
  if (isGroup(scope)) {
    return scope.rules.reduce(
      (ruleCount, childScope) => ruleCount + countFilterRules(childScope),
      0,
    );
  }
  return 0;
}

/**
 * Count the rules the person set in the filter builder. The list search box
 * adds a rule of its own, and that one is left out. The search box already
 * shows that text, so counting it on the funnel too would count it twice.
 */
export function countFilterRulesExceptListSearch(scope: IScope): number {
  if (isRule(scope)) {
    return scope.id === LIST_SEARCH_RULE_ID ? 0 : 1;
  }
  if (isGroup(scope)) {
    return scope.rules.reduce(
      (ruleCount, childScope) => ruleCount + countFilterRulesExceptListSearch(childScope),
      0,
    );
  }
  return 0;
}

/**
 * The filter trigger's label text: "Filter" when no rules are set, else
 * "1 rule" / "N rules". The list search box's rule is not counted. Shared between FilterDialog (which renders it) and
 * EditableSearchTableHeader (which watches it to invalidate its toolbar
 * width caches when the label — and therefore the trigger width — changes).
 */
export function filterRuleCountLabel(filter: IScope | null | undefined): string {
  const ruleCount = filter && isGroup(filter) ? countFilterRulesExceptListSearch(filter) : 0;
  if (ruleCount === 0) {
    return 'Filter';
  }
  return ruleCount === 1 ? '1 rule' : `${ruleCount} rules`;
}
